/**
 * Shape Designer library API client.
 *
 * Talks to the server-backed shape library (`/api/shapes`). Each stored
 * record is a SavedShape plus ownership/visibility metadata: the caller sees
 * their own shapes PLUS every public one. The JWT is attached globally by the
 * fetch interceptor (see api/fetchInterceptor.ts), so these are plain fetches.
 */

import type { SavedShape } from '../types/designShape';
import { API_BASE } from './apiBase';

/** List the caller's own shapes plus all public ones. */
export async function listShapes(): Promise<SavedShape[]> {
  const res = await fetch(`${API_BASE}/api/shapes`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.shapes) ? json.shapes : [];
}

/** Create or overwrite an OWNED shape. Owner/visibility are set server-side. */
export async function saveShape(
  shape: SavedShape,
): Promise<{ success: boolean; id: string; visibility: 'private' | 'public'; owner: string }> {
  const body: SavedShape = {
    id: shape.id,
    name: shape.name,
    savedAt: shape.savedAt,
    group: shape.group,
  };
  const res = await fetch(`${API_BASE}/api/shapes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text);
  }
  return res.json();
}

/** Delete a shape (owner only; the backend 403s otherwise). */
export async function deleteShape(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/api/shapes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text);
  }
}

/** Publish/unpublish a shape (owner only; the backend 403s otherwise). */
export async function setShapeVisibility(
  id: string,
  isPublic: boolean,
): Promise<{ success: boolean; id: string; visibility: 'private' | 'public' }> {
  const res = await fetch(`${API_BASE}/api/shapes/${encodeURIComponent(id)}/visibility`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ public: isPublic }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => `HTTP ${res.status}`);
    throw new Error(text);
  }
  return res.json();
}

/** Fetch a single shape's full record. */
export async function getShape(id: string): Promise<SavedShape> {
  const res = await fetch(`${API_BASE}/api/shapes/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
